<template>
  <LayoutSplit
    :show-account="true"
    :show-report-problem="true"
    :show-go-back-map="true"
  >
    <template #left>
      <MetadataPrimary
        class="pb-3 px-8 py-6"
        :image="currentImage"
      />
      <hr
        v-if="!modeObservation"
        class="separator"
      >
      <MetadataSecondary
        v-if="!modeObservation"
        class="information-height px-8 py-6"
        :image="currentImage"
      />
      <hr
        v-if="!modeObservation"
        class="separator"
      >

      <div
        v-if="modeObservation"
        class="no-margin"
      >
        <p class="pb-6 px-8">
          {{ $t('layout.observations.readme') }}
        </p>
        <ObsList
          :image-id="currentImage.id"
        />
        <ObsItemEdit
          v-if="editingObs || addingObs"
          :image-id="currentImage.id"
        />
      </div>
    </template>

    <template #right>
      <Viewer2D
        v-if="!$isPreRenderAgent"
        :mode-observation="modeObservation"
        :current-image="currentImage"
        :is-current-image-loaded="isCurrentImageLoaded"
      />
    </template>
  </LayoutSplit>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { useToast } from 'vue-toastification';

import { checkLock } from '@/api/image';
import LayoutSplit from '@/layouts/LayoutSplit';
import LockWorker from 'worker-loader!../../utils/lockWorker.js';
import MetadataPrimary from '@/components/explorer/ImageMetaPrimary';
import MetadataSecondary from '@/components/explorer/ImageMetaSecondary';

import ObsItemEdit from '@/components/observations/ObservationsEditItem';
import ObsList from '@/components/observations/ObservationsList';

import Viewer2D from '@/components/observations/Viewer2DObservations';

export default {
  name: 'ContributeId',

  components: {
    LayoutSplit,
    MetadataPrimary,
    MetadataSecondary,
    Viewer2D,
    ObsItemEdit,
    ObsList
  },

  metaInfo() {
    return {
      title: this.currentImage.title,
      meta: [
        ...(this.currentImage.caption ? [{ vmid: 'description', name: 'description', content: this.currentImage.caption }] : []),
        ...(this.currentImage.caption ? [{ vmid: 'og:description', property: 'og:description', content: this.currentImage.caption }] : []),
        { vmid: 'thumbnail', property: 'thumbnail', content: this.currentImage?.media?.image_url },
        { vmid: 'og:image', property: 'og:image', content: this.currentImage?.media?.image_url },
        { vmid: 'og:image:width', property: 'og:image:width', content: '500' },
        { vmid: 'twitter:image', property: 'twitter:image', content: this.currentImage?.media?.image_url }
      ]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (from.fullPath.startsWith('/geolocalise/') && vm.geolocaliseAgain) {
        vm.setGeolocaliseAgain(false);
      }
    });
  },

  beforeRouteLeave(to, from) {
    if (to.name === 'ProvideLocation') {
      checkLock({ id: this.currentImage.id })
        .then(res => {
          if (res.data.locked && res.data.user_id !== this.userInfo.id) {
            this.lockedAfterCheck = true;
            this.toast.info(this.$t('pages.contribute.imageLocked'));
            return false;
          } else {
            return true;
          }
        });
    } else {
      return true;
    }
  },

  setup() {
    const toast = useToast();
    let unsubscribeLang = null;
    let lockWorker = new LockWorker();

    return { unsubscribeLang, lockWorker, toast };
  },

  computed: {
    ...mapState('app', ['previousCurrentRoute', 'modeObservation']),
    ...mapState('geolocalisation', ['geolocaliseAgain']),
    ...mapState('user', ['userInfo']),
    ...mapGetters('user', ['isValidator']),
    ...mapState('explorer', ['images', 'currentImage', 'currentPose']),
    ...mapGetters('explorer', ['isCurrentImageLoaded']),
    ...mapGetters('observations', ['loadedObsToDisplay', 'addingObs', 'editingObs'])
  },

  watch: {
    // call again the method if the route changes
    '$route': {
      handler() {
        if(this.$route.path === '/contribute/') {
          return;
        }
        this.loadImage();
      },

      immediate: true
    }
  },

  created() {
    this.unsubscribeLang = this.$store.watch(
      (state, getters) => state.user.userInfo.lang,
      (oldValue, newValue ) => {
        if (oldValue !== newValue) {
          // reload info if language changed
          this.loadImage();
        }
      }
    );
  },

  mounted() {
    this.lockWorker.postMessage({ cmd: 'initListen' });
    this.lockWorker.addEventListener('message', this.setLockingState);

    // Show a message if redirected from geolocalise when image is locked by another user
    if (this.$route.query.locked) {
      this.toast.info(this.$t('pages.contribute.imageLocked'));
    }
    // Show a message if redirected from geolocalise when image has already geolocalisation to check
    if (this.$route.query.waiting_validation) {
      this.toast.info(this.$t('pages.contribute.imageLocked'));
    }
  },

  beforeUnmount() {
    this.lockWorker.removeEventListener('message', this.setLockingState);
    this.lockWorker.postMessage({ cmd: 'closeListen' });
    this.lockWorker = null;
    if(this.unsubscribeLang) this.unsubscribeLang();
  },

  methods: {
    loadImage() {
      this.imageId = this.$route.params.imageId;
      if (!this.imageId) {
        return; // triggered by watched route when navigating back to map
      }

      // Load image attributes
      if ((this.currentImage.state === 'validated' && this.geolocaliseAgain) || (this.$route.name !== 'ContributeProblem' && this.previousCurrentRoute[0] !== 'ContributeProblem')) { // Don't reload image with child route /problem
        this.loadImageContribute({ id: this.imageId })
          .then(() => {
            window.prerenderReady = true; // For pre-rendering SEO purpose

            let latitude = this.currentImage?.apriori_locations[0]?.latitude;
            let longitude = this.currentImage?.apriori_locations[0]?.longitude;
            this.updateFilters({ latitude, longitude });

            // Image not published can be see by validator and admin otherwise
            if (!this.currentImage.is_published && !this.isValidator) {
              this.toast.error(this.$t('generic.imageNotPublic'));
              this.$router.replace({ path: '/contribute' });
            }

            // Redirect to contribute if image has already been validated but needs improvement
            if (this.currentImage.state === 'validated' && this.geolocaliseAgain) {
              this.$router.push({ path: `/geolocalise/${this.imageId}/align` });
            }

            if (this.currentImage.state === 'validated' && !this.geolocaliseAgain) {
              this.$router.push({ path: `/visit/${this.imageId}` });
            }

            // Disabled observation mode
            if (this.currentImage.observation_enabled === false && this.modeObservation) {
              this.toggleModeObservation();
            }

            // Load images for slider (maybe there is a better way?) when user arrive on page not from explorer map (reload for example)
            if (this.images.length === 0) {
              this.updateFilters({ reloadImages: true });
            }
          })
          .catch(error => {
            if (error.response && error.response.status === 404) {
              this.$router.push({ name: 'Error404' });
            }
          });
      }
    },

    setLockingState(e) {
      const id = parseInt(e.data?.value?.id, 10);
      const user_id = parseInt(e.data?.value?.user_id, 10);
      const cmd = e.data?.cmd;

      const isImageSame = parseInt(this.$route.params.imageId, 10) === id;

      if (isImageSame) {
        if (cmd === 'lock') {
          this.LOCK_CURRENT_IMAGE();
        }
        if (cmd === 'unlock') {
          this.UNLOCK_CURRENT_IMAGE();
        }
        this.SET_LOCKED_USER_ID(user_id);
      }
    },

    ...mapMutations('explorer', ['LOCK_CURRENT_IMAGE', 'UNLOCK_CURRENT_IMAGE', 'SET_LOCKED_USER_ID']),
    ...mapActions('app', ['toggleModeObservation']),
    ...mapActions('explorer', ['loadImageContribute', 'updateFilters']),
    ...mapActions('geolocalisation', ['setGeolocaliseAgain']),
  }
};
</script>

<style lang="postcss" scoped>
.information-height {
    --informationHeight: calc(100px + (1.5rem * 2));
}
</style>
